/*
 * Môdern Studio™ Cloud Projects -- adapter (Phase 5D.3A)
 * ------------------------------------------------------------
 * Talks to the Phase 5C /api/proxy/projects and /api/proxy/homes routes
 * through the Shopify App Proxy.
 *
 * Current rollout:
 *   READS:
 *     - listProjects
 *     - getProject
 *     - listHomes
 *     - getHome
 *
 *   CONTROLLED WRITE SURFACE:
 *     - createProject
 *     - attachMoodBoard
 *     - attachSpacePlan
 *     - deleteProject
 *     - assignProjectHome
 *     - createHome
 *     - deleteHome
 *     - editHome (Phase 6C: Home rename only -- op, id, version, name)
 *
 * No other mutation is exposed yet.
 *
 * This file must never:
 *   - write cloud data into localStorage
 *   - construct or send a customerId, email, or other identity value
 *   - bypass the Shopify App Proxy
 *   - call import, or edit anything but a Home's name (Phase 6C), until
 *     those operations are introduced deliberately in later rollout steps
 *
 * Identity comes only from Shopify's signed App Proxy request server-side.
 *
 * Relative same-origin App Proxy paths are used exclusively:
 *   /apps/modern-studio/projects
 *   /apps/modern-studio/homes
 *
 * The backend remains the authority for:
 *   - feature flags
 *   - write rollout
 *   - entitlement
 *   - customer ownership
 *   - input validation
 *   - optimistic concurrency
 *
 * When STUDIO_CLOUD_PROJECTS_WRITES_ENABLED is off, createProject(),
 * attachMoodBoard(), attachSpacePlan(), deleteProject(),
 * assignProjectHome(), createHome(), and deleteHome() will receive the
 * backend's fail-closed 404 response and return a non-ok result. The
 * adapter does not attempt to bypass or reinterpret that protection.
 *
 * Status contract:
 *
 *   {
 *     ok: boolean,
 *     status:
 *       'ok'
 *       | 'cloud_unavailable'
 *       | 'not_authenticated'
 *       | 'not_entitled'
 *       | 'conflict'
 *       | 'network_error'
 *       | 'server_error'
 *       | 'error',
 *     data,
 *     raw,
 *     httpStatus,        // Phase 6A-2; null when no response arrived
 *     outcomeUnknown     // Phase 6A-2; true for network errors and 5xx
 *   }
 *
 * Phase 6A-2 (conflict recovery):
 *   - A 409 on a write returns status 'conflict' with
 *     data = { current: <normalized record> | null }. `current` is null
 *     unless the server's copy is for the same id and has a different
 *     integer version than the one we sent.
 *   - MSGCloudProjects.recovery holds the shared recovery helpers and the
 *     createToolSaver() controller used by Mood Board and Space Planner.
 *     Rules: a conflicted attach is retried at most once, and only when the
 *     field being written is unchanged on the server; a changed field is
 *     shown to the customer as a choice, and until they choose "Replace"
 *     the ordinary "Add to Project" button shows that choice again instead
 *     of writing. A record read after a 409 without a usable copy, or
 *     before writing to a Project whose version was unknown, is compared
 *     the same way before anything is sent. Deletes are never retried. A 409,
 *     a network error, or a 5xx is never reported as a confirmed save.
 *     Nothing is written to localStorage or sessionStorage.
 *
 * A rolling in-memory diagnostic log is available through getDiagnostics().
 * It is never persisted.
 */
(function () {
  var PROXY_BASE = '/apps/modern-studio';
  var MAX_DIAGNOSTIC_ENTRIES = 25;
  var diagnostics = [];

  function recordDiagnostic(entry) {
    diagnostics.push(
      Object.assign(
        {
          at: Date.now(),
        },
        entry
      )
    );

    if (diagnostics.length > MAX_DIAGNOSTIC_ENTRIES) {
      diagnostics.shift();
    }
  }

  function getDiagnostics() {
    return diagnostics.slice();
  }

  // ------------------------------------------------------------
  // Normalization
  // ------------------------------------------------------------

  function normalizeProject(p) {
    if (!p) return null;

    return {
      id: p.id,
      clientLegacyId: p.clientLegacyId || null,
      homeId: p.homeId || null,
      name: p.name,
      room: p.room,
      roomLabel: p.roomLabel || null,
      moodBoard: p.moodBoard || null,
      spacePlan: p.spacePlan || null,
      version: p.version,
      updatedAt: p.updatedAt || null,
    };
  }

  function normalizeHome(h) {
    if (!h) return null;

    return {
      id: h.id,
      clientLegacyId: h.clientLegacyId || null,
      name: h.name,
      version: h.version,
      updatedAt: h.updatedAt || null,
    };
  }

  // ------------------------------------------------------------
  // Result classification
  // ------------------------------------------------------------

  // Phase 6A-2: every result now also carries `httpStatus` (null when no
  // response arrived) and `outcomeUnknown` (true only when the request may
  // have been applied but no definite answer came back: a network failure
  // or a 5xx). 403 is reported as 'not_entitled' and 5xx as 'server_error'.
  // 400, 401, 403, 404, 409 and 429 are definite: the backend wrote nothing.
  function classifyResponse(res, body) {
    var status = res.status;

    if (status === 200) {
      return {
        ok: true,
        status: 'ok',
        data: body,
        raw: body,
        httpStatus: status,
        outcomeUnknown: false,
      };
    }

    var result = {
      ok: false,
      status: 'error',
      data: null,
      raw: body,
      httpStatus: status,
      outcomeUnknown: false,
    };

    if (status === 404) {
      result.status = 'cloud_unavailable';
    } else if (status === 401) {
      result.status = 'not_authenticated';
    } else if (status === 403) {
      result.status = 'not_entitled';
    } else if (status === 409) {
      result.status = 'conflict';
    } else if (status >= 500) {
      result.status = 'server_error';
      result.outcomeUnknown = true;
    }

    return result;
  }

  function networkErrorResult() {
    return {
      ok: false,
      status: 'network_error',
      data: null,
      raw: null,
      httpStatus: null,
      outcomeUnknown: true,
    };
  }

  // Phase 6A-2: a 409 body is { error: 'version_conflict', current }.
  // `current` is only accepted when it is plausibly the record this request
  // targeted: an object whose id equals the requested id and whose version
  // is an integer different from the version that was sent. Anything else
  // is treated as "no usable current record" (data.current === null), and
  // callers fall back to an explicit read.
  function isUsableCurrent(current, requestedId, sentVersion) {
    return !!(
      current &&
      typeof current === 'object' &&
      !Array.isArray(current) &&
      current.id === requestedId &&
      typeof current.version === 'number' &&
      Math.floor(current.version) === current.version &&
      current.version !== sentVersion
    );
  }

  function attachConflictCurrent(result, kind, requestedId, sentVersion) {
    if (!result || result.status !== 'conflict') return result;

    var raw = result.raw && result.raw.current;
    var usable = isUsableCurrent(raw, requestedId, sentVersion);

    result.data = {
      current: usable
        ? (kind === 'home' ? normalizeHome(raw) : normalizeProject(raw))
        : null,
    };

    return result;
  }

  // ------------------------------------------------------------
  // Core GET wrapper
  // ------------------------------------------------------------

  // Phase 6C: an optional time limit for one request. Used only by Home
  // rename and its recovery reads (opts.timeoutMs); every other caller
  // passes no limit and behaves exactly as before. When the limit passes
  // first, the request is aborted, its result is TIMED OUT (outcome
  // unknown), and a reply that arrives later is ignored.
  function timedOutResult(op, path, method) {
    recordDiagnostic({
      op: op,
      path: path,
      method: method,
      httpStatus: null,
      resultStatus: 'timeout',
    });
    return {
      ok: false,
      status: 'network_error',
      data: null,
      raw: null,
      httpStatus: null,
      outcomeUnknown: true,
      timedOut: true,
    };
  }

  function withTimeLimit(promise, timeoutMs, controller, onTimeout) {
    if (!timeoutMs) return promise;
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) {
          try { controller.abort(); } catch (e) { /* ignore */ }
        }
        resolve(onTimeout());
      }, timeoutMs);
      promise.then(function (value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      }, function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(onTimeout());
      });
    });
  }

  function newController(opts) {
    return opts && opts.timeoutMs && typeof AbortController === 'function'
      ? new AbortController()
      : null;
  }

  function cloudGet(path, op, opts) {
    var controller = newController(opts);
    var init = {
      method: 'GET',
      credentials: 'same-origin',
    };
    if (controller) init.signal = controller.signal;

    return withTimeLimit(fetch(PROXY_BASE + path, init)
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            var result = classifyResponse(res, body);

            recordDiagnostic({
              op: op,
              path: path,
              method: 'GET',
              httpStatus: res.status,
              resultStatus: result.status,
            });

            return result;
          });
      })
      .catch(function (err) {
        var result = networkErrorResult();

        recordDiagnostic({
          op: op,
          path: path,
          method: 'GET',
          httpStatus: null,
          resultStatus: result.status,
          message: err && err.message,
        });

        return result;
      }), opts && opts.timeoutMs, controller, function () {
        return timedOutResult(op, path, 'GET');
      });
  }

  // ------------------------------------------------------------
  // Core POST wrapper
  // ------------------------------------------------------------

  function cloudPost(path, payload, op, opts) {
    // Phase 6B #3: last line of defense -- no account write is sent unless
    // this page confirmed account saving is on (both list replies said so).
    // Fails closed: no status function (e.g. an older studio-projects.js)
    // means no account write either.
    var store = window.MSGProjects;
    if (!store || typeof store.getAccountWrites !== 'function' || store.getAccountWrites() !== 'on') {
      recordDiagnostic({ op: op, path: path, method: 'POST', httpStatus: null, resultStatus: 'writes_paused' });
      return Promise.resolve({
        ok: false,
        status: 'writes_paused',
        data: null,
        raw: null,
        httpStatus: null,
        outcomeUnknown: false,
      });
    }
    var controller = newController(opts);
    var init = {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };
    if (controller) init.signal = controller.signal;

    return withTimeLimit(fetch(PROXY_BASE + path, init)
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            var result = classifyResponse(res, body);

            recordDiagnostic({
              op: op,
              path: path,
              method: 'POST',
              httpStatus: res.status,
              resultStatus: result.status,
            });

            // Phase 6B #3: a refused CREATE (404) means account saving was
            // switched off during this visit. The page switches to device
            // saving and is told, so it can say so (nothing was saved).
            if (result.status === 'cloud_unavailable' && payload && payload.op === 'create' && store &&
                typeof store.setAccountWrites === 'function') {
              store.setAccountWrites('off');
              try { window.dispatchEvent(new CustomEvent('msg:account-writes-paused', { detail: { op: op } })); } catch (e) { /* ignore */ }
            }

            return result;
          });
      })
      .catch(function (err) {
        var result = networkErrorResult();

        recordDiagnostic({
          op: op,
          path: path,
          method: 'POST',
          httpStatus: null,
          resultStatus: result.status,
          message: err && err.message,
        });

        return result;
      }), opts && opts.timeoutMs, controller, function () {
        return timedOutResult(op, path, 'POST');
      });
  }

  // ------------------------------------------------------------
  // Project reads
  // ------------------------------------------------------------

  // Phase 6B #3: true / false only when the list reply says so explicitly.
  function writesStatusOf(raw) {
    return raw && typeof raw.writesEnabled === 'boolean' ? raw.writesEnabled : null;
  }

  function listProjects(opts) {
    return cloudGet('/projects', 'listProjects', opts).then(function (result) {
      if (!result.ok) return result;

      var list = (result.raw && result.raw.projects) || [];

      // Phase 6B #3: account-saving status; anything but a boolean is unknown (null).
      return Object.assign({}, result, {
        data: list.map(normalizeProject),
        writesEnabled: writesStatusOf(result.raw),
      });
    });
  }

  function getProject(id) {
    if (!id) {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    return cloudGet(
      '/projects?id=' + encodeURIComponent(id),
      'getProject'
    ).then(function (result) {
      if (!result.ok) return result;

      return Object.assign({}, result, {
        data: normalizeProject(
          result.raw && result.raw.project
        ),
      });
    });
  }

  // ------------------------------------------------------------
  // Project create
  // ------------------------------------------------------------

  function createProject(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Deliberately whitelist only fields accepted by the backend create
     * contract. Never forward customerId, id, version, homeId, timestamps,
     * or arbitrary caller properties.
     */
    var payload = {
      op: 'create',
      name: input.name,
      room: input.room,
    };

    if (input.roomLabel !== undefined) {
      payload.roomLabel = input.roomLabel;
    }

    if (input.moodBoard !== undefined) {
      payload.moodBoard = input.moodBoard;
    }

    if (input.spacePlan !== undefined) {
      payload.spacePlan = input.spacePlan;
    }

    return cloudPost(
      '/projects',
      payload,
      'createProject'
    ).then(function (result) {
      if (!result.ok) return result;

      var project =
        result.raw &&
        result.raw.project
          ? normalizeProject(result.raw.project)
          : null;

      if (!project) {
        recordDiagnostic({
          op: 'createProject',
          path: '/projects',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing project in successful response.',
        });

        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
        };
      }

      return Object.assign({}, result, {
        data: project,
      });
    });
  }

  // ------------------------------------------------------------
  // Project attach (add a Mood Board to an existing Project)
  // ------------------------------------------------------------

  function attachMoodBoard(projectId, version, boardData) {
    if (!projectId || typeof version !== 'number') {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Deliberately whitelist only fields accepted by the backend attach
     * contract. Never forward customerId, homeId, name, room, timestamps,
     * or arbitrary caller properties -- only the identifiers required for
     * optimistic concurrency and the moodBoard payload itself.
     */
    var payload = {
      op: 'attach',
      id: projectId,
      version: version,
      moodBoard: boardData,
    };

    return cloudPost(
      '/projects',
      payload,
      'attachMoodBoard'
    ).then(function (result) {
      attachConflictCurrent(result, 'project', projectId, version);
      if (!result.ok) return result;

      var project =
        result.raw &&
        result.raw.project
          ? normalizeProject(result.raw.project)
          : null;

      if (!project) {
        recordDiagnostic({
          op: 'attachMoodBoard',
          path: '/projects',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing project in successful response.',
        });

        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
        };
      }

      return Object.assign({}, result, {
        data: project,
      });
    });
  }


  // ------------------------------------------------------------
  // Project attach (add a Space Plan to an existing Project)
  // ------------------------------------------------------------

  function attachSpacePlan(projectId, version, spacePlanData) {
    if (!projectId || typeof version !== 'number') {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Deliberately whitelist only fields accepted by the backend attach
     * contract. Never forward customerId, homeId, name, room, timestamps,
     * or arbitrary caller properties -- only the identifiers required for
     * optimistic concurrency and the spacePlan payload itself. The
     * spacePlan payload shape ({collection, roomLabel, length, width,
     * bedSize, ownedItems, savedAt}) is passed through exactly as the
     * caller built it -- Phase 5D Stage 1 extended the backend validator
     * to accept this shape natively, so no reshaping happens here.
     */
    var payload = {
      op: 'attach',
      id: projectId,
      version: version,
      spacePlan: spacePlanData,
    };

    return cloudPost(
      '/projects',
      payload,
      'attachSpacePlan'
    ).then(function (result) {
      attachConflictCurrent(result, 'project', projectId, version);
      if (!result.ok) return result;

      var project =
        result.raw &&
        result.raw.project
          ? normalizeProject(result.raw.project)
          : null;

      if (!project) {
        recordDiagnostic({
          op: 'attachSpacePlan',
          path: '/projects',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing project in successful response.',
        });

        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
        };
      }

      return Object.assign({}, result, {
        data: project,
      });
    });
  }

  // ------------------------------------------------------------
  // Project delete
  // ------------------------------------------------------------

  function deleteProject(projectId, version) {
    if (!projectId || typeof version !== 'number') {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Deliberately whitelist only fields accepted by the backend delete
     * contract -- op, id, and the version required for optimistic
     * concurrency. Never forward customerId or any other caller property.
     * A successful delete returns { deleted: true }, not a project, so
     * this does not run the response through normalizeProject().
     */
    var payload = {
      op: 'delete',
      id: projectId,
      version: version,
    };

    return cloudPost(
      '/projects',
      payload,
      'deleteProject'
    ).then(function (result) {
      attachConflictCurrent(result, 'project', projectId, version);
      if (!result.ok) return result;

      var deleted =
        !!(result.raw && result.raw.deleted === true);

      if (!deleted) {
        recordDiagnostic({
          op: 'deleteProject',
          path: '/projects',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing deleted confirmation in successful response.',
        });

        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
        };
      }

      return Object.assign({}, result, {
        data: { id: projectId, deleted: true },
      });
    });
  }

  // ------------------------------------------------------------
  // Project Home assignment
  // ------------------------------------------------------------

  function assignProjectHome(projectId, version, homeId) {
    if (!projectId || typeof version !== 'number') {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    if (homeId !== null && typeof homeId !== 'string') {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Deliberately whitelist only fields accepted by the backend assign
     * contract -- op, id, version, and homeId (a UUID string to assign a
     * Home, or null to unassign). Never forward customerId or any other
     * caller property.
     */
    var payload = {
      op: 'assign',
      id: projectId,
      version: version,
      homeId: homeId,
    };

    return cloudPost(
      '/projects',
      payload,
      'assignProjectHome'
    ).then(function (result) {
      attachConflictCurrent(result, 'project', projectId, version);
      if (!result.ok) return result;

      var project =
        result.raw &&
        result.raw.project
          ? normalizeProject(result.raw.project)
          : null;

      if (!project) {
        recordDiagnostic({
          op: 'assignProjectHome',
          path: '/projects',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing project in successful response.',
        });

        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
        };
      }

      return Object.assign({}, result, {
        data: project,
      });
    });
  }

  // ------------------------------------------------------------
  // Home create
  // ------------------------------------------------------------

  function createHome(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Deliberately whitelist only the field accepted by the backend Home
     * create contract -- op and name. Home creation takes no version (the
     * backend assigns version 1 on insert) and no other field. Never
     * forward customerId, id, or arbitrary caller properties.
     */
    var payload = {
      op: 'create',
      name: input.name,
    };

    return cloudPost(
      '/homes',
      payload,
      'createHome'
    ).then(function (result) {
      if (!result.ok) return result;

      var home =
        result.raw &&
        result.raw.home
          ? normalizeHome(result.raw.home)
          : null;

      if (!home) {
        recordDiagnostic({
          op: 'createHome',
          path: '/homes',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing home in successful response.',
        });

        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
        };
      }

      return Object.assign({}, result, {
        data: home,
      });
    });
  }

  // ------------------------------------------------------------
  // Home delete
  // ------------------------------------------------------------

  function deleteHome(homeId, version) {
    if (!homeId || typeof version !== 'number') {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Deliberately whitelist only fields accepted by the backend delete
     * contract -- op, id, and the version required for optimistic
     * concurrency. Never forward customerId or any other caller property.
     * A successful delete returns { deleted: true }, not a home, so this
     * does not run the response through normalizeHome(). The backend
     * itself treats `version` as optional on this route (an omitted
     * version deletes unconditionally), but this adapter always requires
     * and sends it -- same precedent as deleteProject() above -- so a
     * stale client can never delete a Home it hasn't actually seen the
     * current state of.
     */
    var payload = {
      op: 'delete',
      id: homeId,
      version: version,
    };

    return cloudPost(
      '/homes',
      payload,
      'deleteHome'
    ).then(function (result) {
      attachConflictCurrent(result, 'home', homeId, version);
      if (!result.ok) return result;

      var deleted =
        !!(result.raw && result.raw.deleted === true);

      if (!deleted) {
        recordDiagnostic({
          op: 'deleteHome',
          path: '/homes',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing deleted confirmation in successful response.',
        });

        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
        };
      }

      return Object.assign({}, result, {
        data: { id: homeId, deleted: true },
      });
    });
  }

  // ------------------------------------------------------------
  // Home rename (Phase 6C)
  // ------------------------------------------------------------

  function editHome(homeId, version, name, opts) {
    if (!homeId || typeof version !== 'number' || typeof name !== 'string') {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    /*
     * Whitelist only the fields of the backend's existing Home edit
     * contract: op, id, the version for optimistic concurrency, and name.
     * Never forward customerId or any other caller property. The name is
     * sent exactly as given (the page trims it, as "+ Create a Home" does);
     * the backend stays the authority on what it accepts.
     */
    var payload = {
      op: 'edit',
      id: homeId,
      version: version,
      name: name,
    };

    return cloudPost(
      '/homes',
      payload,
      'editHome',
      opts
    ).then(function (result) {
      attachConflictCurrent(result, 'home', homeId, version);
      if (!result.ok) return result;

      var home =
        result.raw && result.raw.home
          ? normalizeHome(result.raw.home)
          : null;

      if (!home || home.id !== homeId) {
        recordDiagnostic({
          op: 'editHome',
          path: '/homes',
          method: 'POST',
          httpStatus: 200,
          resultStatus: 'error',
          message: 'Missing or different home in successful response.',
        });

        // A 200 without the Home it was about can't be read as a
        // confirmed rename; the caller checks the Home instead.
        return {
          ok: false,
          status: 'error',
          data: null,
          raw: result.raw,
          httpStatus: 200,
          outcomeUnknown: true,
        };
      }

      return Object.assign({}, result, {
        data: home,
      });
    });
  }

  // ------------------------------------------------------------
  // Home reads
  // ------------------------------------------------------------

  function listHomes(opts) {
    return cloudGet('/homes', 'listHomes', opts).then(function (result) {
      if (!result.ok) return result;

      var list = (result.raw && result.raw.homes) || [];

      return Object.assign({}, result, {
        data: list.map(normalizeHome),
        writesEnabled: writesStatusOf(result.raw),
      });
    });
  }

  function getHome(id, opts) {
    if (!id) {
      return Promise.resolve({
        ok: false,
        status: 'error',
        data: null,
        raw: null,
      });
    }

    return cloudGet(
      '/homes?id=' + encodeURIComponent(id),
      'getHome',
      opts
    ).then(function (result) {
      if (!result.ok) return result;

      return Object.assign({}, result, {
        data: normalizeHome(
          result.raw && result.raw.home
        ),
      });
    });
  }

  // ============================================================
  // Phase 6A-2: conflict-aware recovery
  // ============================================================
  //
  // Shared by Mood Board, Space Planner and My Projects. Rules (approved
  // Phase 6A design):
  //
  //   - Every 409 updates the page's copy of the record in place
  //     (MSGProjects.refreshCloudProject) before anything else happens.
  //   - One customer action may be resent automatically at most once, and
  //     only when the field being written is unchanged in the current
  //     record (canon(reference[field]) === canon(current[field])).
  //   - Deletes are never resent automatically.
  //   - A 409 or an unknown outcome (network failure / 5xx) is never
  //     reported as a confirmed save. Only an HTTP 200 carrying the record
  //     is "saved".
  //   - A write 404 is checked with one follow-up read of the Project list:
  //     present -> saving is unavailable; absent -> deleted elsewhere;
  //     read failed -> unreachable (page copy left as it was).
  //   - Nothing here writes to localStorage or sessionStorage.

  // Deterministic encoding used for every "is this field the same?"
  // comparison: object keys sorted, keys whose value is undefined dropped,
  // null and missing both encode as null, arrays kept in order, strings and
  // numbers exactly as JSON writes them (no trimming or case folding).
  function canon(value) {
    if (value === undefined || value === null) return 'null';
    if (Array.isArray(value)) {
      return '[' + value.map(canon).join(',') + ']';
    }
    if (typeof value === 'object') {
      return '{' + Object.keys(value)
        .filter(function (key) { return value[key] !== undefined; })
        .sort()
        .map(function (key) { return JSON.stringify(key) + ':' + canon(value[key]); })
        .join(',') + '}';
    }
    var encoded = JSON.stringify(value);
    return encoded === undefined ? 'null' : encoded;
  }

  function sameValue(a, b) {
    return canon(a) === canon(b);
  }

  // 'field_unchanged' -> safe to resend once with current.version.
  // 'field_changed'   -> the customer must choose.
  function classifyFieldConflict(reference, current, field) {
    return sameValue(reference && reference[field], current && current[field])
      ? 'field_unchanged'
      : 'field_changed';
  }

  // 'already_in_target' -> the Project already has the requested Home
  //                        (described factually, not as "your change saved").
  // 'field_unchanged'   -> Home membership unchanged; safe to resend once.
  // 'moved'             -> Home changed elsewhere; the customer chooses again.
  function classifyAssignConflict(reference, current, targetHomeId) {
    var currentHome = (current && current.homeId) || null;
    if (currentHome === (targetHomeId || null)) return 'already_in_target';
    if (currentHome === ((reference && reference.homeId) || null)) return 'field_unchanged';
    return 'moved';
  }

  function joinPhrases(list) {
    if (list.length <= 1) return list.join('');
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }

  function homeName(homes, id) {
    var home = (homes || []).filter(function (h) { return h && h.id === id; })[0];
    return home ? home.name : null;
  }

  // Plain-language list of what changed between the record the page showed
  // and the record the server returned. Used after a conflicted delete.
  function describeProjectChanges(before, after, homes) {
    var changes = [];
    if (!before || !after) return changes;

    if (before.name !== after.name) {
      changes.push('it was renamed to “' + after.name + '”');
    }
    if ((before.roomLabel || before.room) !== (after.roomLabel || after.room)) {
      changes.push('its room was changed to ' + (after.roomLabel || after.room));
    }
    if ((before.homeId || null) !== (after.homeId || null)) {
      var toName = after.homeId ? homeName(homes, after.homeId) : null;
      var fromName = before.homeId ? homeName(homes, before.homeId) : null;
      if (after.homeId) {
        changes.push(toName ? 'it was moved to “' + toName + '”' : 'it was moved to a different Home');
      } else {
        changes.push(fromName ? 'it was removed from “' + fromName + '”' : 'it was removed from its Home');
      }
    }
    [['moodBoard', 'Mood Board'], ['spacePlan', 'Space Plan']].forEach(function (pair) {
      var a = before[pair[0]], b = after[pair[0]];
      if (!a && b) changes.push('a ' + pair[1] + ' was added');
      else if (a && !b) changes.push('its ' + pair[1] + ' was removed');
      else if (a && b && !sameValue(a, b)) changes.push('its ' + pair[1] + ' was updated');
    });
    return changes;
  }

  function projectsStore() {
    return window.MSGProjects || null;
  }

  // Replace the page's in-memory copy of one Project, keeping its position.
  function refreshPageProject(project) {
    var store = projectsStore();
    if (!store || !project) return false;
    if (typeof store.refreshCloudProject === 'function') {
      return store.refreshCloudProject(project);
    }
    return false;
  }

  // One read of the Project list to decide what a write 404 meant.
  // Resolves to { state: 'present', record } | { state: 'absent' } |
  // { state: 'unknown' }.
  function checkProjectExists(projectId) {
    return listProjects().then(function (result) {
      if (!result || result.ok !== true || !Array.isArray(result.data)) {
        return { state: 'unknown' };
      }
      var record = result.data.filter(function (p) { return p && p.id === projectId; })[0];
      return record ? { state: 'present', record: record } : { state: 'absent' };
    }, function () {
      return { state: 'unknown' };
    });
  }

  // Authoritative reload of both lists into the page's snapshot (same
  // all-or-nothing rule as the Phase 5D Home-delete refresh). Resolves to
  // { ok: true, projects, homes } or { ok: false }.
  function refreshSnapshotFromCloud() {
    return Promise.all([listProjects(), listHomes()]).then(function (results) {
      var p = results[0], h = results[1];
      var ok = p && p.ok === true && Array.isArray(p.data) && h && h.ok === true && Array.isArray(h.data);
      var store = projectsStore();
      if (!ok || !store || typeof store.setCloudSnapshot !== 'function') return { ok: false };
      // Phase 6B #3: a refresh also refreshes the account-saving status.
      var writes = typeof store.combineWrites === 'function' ? store.combineWrites(p, h) : 'unknown';
      if (store.setCloudSnapshot(p.data, h.data, { writes: writes }) !== true) return { ok: false };
      return { ok: true, projects: p.data, homes: h.data };
    }, function () {
      return { ok: false };
    });
  }

  // Projects whose current version this page could not establish (a 409
  // without a usable record and a failed read). Writes to them re-read
  // first. Memory only.
  var staleProjectIds = {};

  function attachFieldFn(field) {
    return field === 'moodBoard' ? attachMoodBoard : attachSpacePlan;
  }

  // After a 404 on a write to an existing Project.
  function resolveWrite404(projectId) {
    return checkProjectExists(projectId).then(function (check) {
      var store = projectsStore();
      if (check.state === 'present') {
        refreshPageProject(check.record);
        return { type: 'unavailable', current: check.record };
      }
      if (check.state === 'absent') {
        if (store && typeof store.removeCloudProject === 'function') store.removeCloudProject(projectId);
        return { type: 'deleted' };
      }
      return { type: 'unreachable' };
    });
  }

  // Read one Project after an unknown outcome (or to "Check again").
  // Resolves to { checked: true, current } | { checked: true, deleted: true }
  // | { checked: false }.
  function readProjectForCheck(projectId) {
    return getProject(projectId).then(function (result) {
      if (result && result.ok === true && result.data && result.data.id === projectId) {
        refreshPageProject(result.data);
        delete staleProjectIds[projectId];
        return { checked: true, current: result.data };
      }
      if (result && result.status === 'cloud_unavailable') {
        return checkProjectExists(projectId).then(function (check) {
          if (check.state === 'absent') {
            var store = projectsStore();
            if (store && typeof store.removeCloudProject === 'function') store.removeCloudProject(projectId);
            return { checked: true, deleted: true };
          }
          if (check.state === 'present') {
            refreshPageProject(check.record);
            delete staleProjectIds[projectId];
            return { checked: true, current: check.record };
          }
          return { checked: false };
        });
      }
      return { checked: false };
    }, function () {
      return { checked: false };
    });
  }

  // Attach a Mood Board / Space Plan to an existing Project with the
  // approved recovery rules. `reference` is the record the customer's
  // action was based on (the page's copy, or the version shown in a
  // conflict panel for "Replace").
  //
  // Resolves to one of:
  //   { type: 'saved', project }
  //   { type: 'conflict', current, fieldChanged }
  //   { type: 'unavailable', current } | { type: 'deleted' } | { type: 'unreachable' }
  //   { type: 'unknown', check }          -- no definite answer; `check` is readProjectForCheck()
  //   { type: 'stale' }                   -- 409 with no usable record and the read failed
  //   { type: 'not_entitled' } | { type: 'not_authenticated' } | { type: 'error' }
  function attachFieldWithRecovery(opts) {
    var field = opts.field;
    var reference = opts.reference;
    var projectId = reference.id;
    var attach = attachFieldFn(field);
    var retriesLeft = 1;

    // Phase 6A-2 (revision): every newly read or newly returned record is
    // compared against the reference BEFORE anything is sent at its version.
    // A changed target field always stops here with the customer's choice;
    // an unchanged one may be sent at the new version, and that send is the
    // one automatic retry this customer action is allowed.
    function afterConflict(current) {
      refreshPageProject(current);
      delete staleProjectIds[projectId];
      if (classifyFieldConflict(reference, current, field) === 'field_unchanged') {
        if (retriesLeft > 0) {
          retriesLeft -= 1;
          return attempt(current.version);
        }
        return { type: 'conflict', current: current, fieldChanged: false };
      }
      return { type: 'conflict', current: current, fieldChanged: true };
    }

    function attempt(version) {
      return attach(projectId, version, opts.value).then(function (result) {
        if (result && result.ok === true && result.data) {
          var store = projectsStore();
          if (store && typeof store.upsertCloudProject === 'function') store.upsertCloudProject(result.data);
          return { type: 'saved', project: result.data };
        }
        var status = result && result.status;
        if (status === 'conflict') {
          var current = result.data && result.data.current;
          if (current) return afterConflict(current);
          return readProjectForCheck(projectId).then(function (check) {
            if (check.checked && check.current) return afterConflict(check.current);
            if (check.checked && check.deleted) return { type: 'deleted' };
            staleProjectIds[projectId] = true;
            return { type: 'stale' };
          });
        }
        if (status === 'cloud_unavailable') return resolveWrite404(projectId);
        if (result && result.outcomeUnknown) {
          return readProjectForCheck(projectId).then(function (check) {
            return { type: 'unknown', check: check };
          });
        }
        if (status === 'not_entitled' || status === 'not_authenticated') return { type: status };
        return { type: 'error' };
      });
    }

    // A Project whose current version this page could not establish earlier
    // is read first. The read is treated exactly like a conflict's current
    // record: compared first, and sent at most once, automatically, only if
    // the target field is unchanged.
    if (staleProjectIds[projectId]) {
      return readProjectForCheck(projectId).then(function (check) {
        if (!check.checked) return { type: 'stale' };
        if (check.deleted) return { type: 'deleted' };
        return afterConflict(check.current);
      });
    }
    return attempt(reference.version);
  }

  // Create a new Project. There is no id to check after an unknown outcome,
  // so the page's Project list is refreshed instead and the customer is
  // asked to look before adding again (no duplicate-matching heuristics).
  function createProjectWithRecovery(input) {
    return createProject(input).then(function (result) {
      if (result && result.ok === true && result.data) {
        var store = projectsStore();
        if (store && typeof store.upsertCloudProject === 'function') store.upsertCloudProject(result.data);
        return { type: 'saved', project: result.data, created: true };
      }
      var status = result && result.status;
      if (status === 'cloud_unavailable') return { type: 'unavailable' };
      if (result && result.outcomeUnknown) {
        return refreshSnapshotFromCloud().then(function (refresh) {
          return { type: 'unknown_create', refreshed: refresh.ok };
        });
      }
      if (status === 'not_entitled' || status === 'not_authenticated') return { type: status };
      return { type: 'error' };
    });
  }

  // ============================================================
  // Phase 6C: Home rename with recovery
  // ============================================================
  //
  // Rules (approved 6C design):
  //   - Only a 200 carrying the same Home, or a read showing the Home now
  //     has the requested name, is reported as the Home's name. Outcomes
  //     describe the Home's confirmed current state; they never say which
  //     tab or device made a change.
  //   - One customer Save allows AT MOST ONE automatic resend, and only
  //     after a 409 when ALL of these hold: the Home in the reply is usable
  //     (same id, whole-number version, not the version sent) -- or, when
  //     it isn't, the Home read right after counts in its place; its name
  //     is EXACTLY the name the customer started from (no trimming or case
  //     folding); its version is HIGHER than the version sent; the resend
  //     hasn't been used yet; and account saving is still 'on'. The resend
  //     carries the same requested name at the newer version. A second 409
  //     is never resent.
  //   - After an unknown outcome (no reply, a 5xx, or the time limit)
  //     nothing is resent; the Home is read instead. Every later send needs
  //     a customer tap.
  //   - Every request here -- the edit and every recovery read -- has a
  //     time limit (renameTiming.requestTimeoutMs). A recovery read that
  //     fails or times out returns { type: 'check' } so the page keeps the
  //     customer's text and offers a read-only "Check again".
  //   - Nothing is written to localStorage or sessionStorage.
  var renameTiming = { requestTimeoutMs: 20000 };

  function renameOpts() {
    return { timeoutMs: renameTiming.requestTimeoutMs };
  }

  function refreshPageHome(home) {
    var store = projectsStore();
    return !!(store && typeof store.refreshCloudHome === 'function' && store.refreshCloudHome(home));
  }

  // Bounded reload of both account lists into the page's snapshot (all or
  // nothing, like refreshSnapshotFromCloud). Also refreshes the saving status.
  function refreshListsWithin() {
    var opts = renameOpts();
    return Promise.all([listProjects(opts), listHomes(opts)]).then(function (results) {
      var p = results[0], h = results[1];
      var ok = p && p.ok === true && Array.isArray(p.data) && h && h.ok === true && Array.isArray(h.data);
      var store = projectsStore();
      if (!ok || !store || typeof store.setCloudSnapshot !== 'function') return { ok: false };
      var writes = typeof store.combineWrites === 'function' ? store.combineWrites(p, h) : 'unknown';
      if (store.setCloudSnapshot(p.data, h.data, { writes: writes }) !== true) return { ok: false };
      return { ok: true, homes: h.data, writes: writes };
    }, function () {
      return { ok: false };
    });
  }

  // `sent` is { name, version }: the name the customer started from and the
  // version the last edit carried. After an unknown outcome a read can only
  // say what the Home is named NOW: an edit that timed out may still be
  // applied later, so an unchanged Home is never reported as "not renamed".
  function describeRead(sent, requested, home) {
    if (home.name === requested) return { type: 'renamed', home: home };
    return { type: 'current', home: home };
  }

  // After a definite 404 (or a Home read that returned 404): the lists say
  // whether saving is paused, the Home is gone, or it is still there.
  // `definite`: the edit was answered (404), so it wasn't applied. After an
  // unknown outcome it is false: the lists can't prove anything was not applied.
  function resolveRename404(homeId, requested, sent, definite) {
    return refreshListsWithin().then(function (refresh) {
      if (!refresh.ok) return { type: 'check', check: 'lists', definite: definite !== false, sent: sent };
      if (refresh.writes !== 'on') return { type: 'paused' };
      var home = refresh.homes.filter(function (h) { return h && h.id === homeId; })[0] || null;
      if (!home) return { type: 'gone' };
      if (home.name === requested) return { type: 'renamed', home: home };
      // Only a definite 404 proves the edit wasn't applied.
      if (definite === false) return { type: 'current', home: home };
      return { type: 'not_renamed', home: home };
    });
  }

  function readHomeWithin(homeId) {
    return getHome(homeId, renameOpts()).then(function (result) {
      if (result && result.ok === true && result.data && result.data.id === homeId) {
        return { state: 'present', home: result.data };
      }
      if (result && result.status === 'cloud_unavailable') return { state: 'absent' };
      return { state: 'unknown' };
    }, function () {
      return { state: 'unknown' };
    });
  }

  function refusedReason(result) {
    var status = result && result.status;
    if (status === 'not_authenticated' || status === 'not_entitled') return status;
    if (result && result.httpStatus === 429) return 'rate_limited';
    if (result && result.httpStatus === 400) return 'invalid';
    return 'error';
  }

  // reference: { id, name, version } -- the Home as the customer last saw it.
  // requested: the trimmed name to save.
  function renameHomeWithRecovery(reference, requested) {
    var homeId = reference.id;
    var retriesLeft = 1;

    function afterConflict(current, sentVersion) {
      refreshPageHome(current);
      if (current.name === requested) return { type: 'renamed', home: current };
      var store = projectsStore();
      if (
        retriesLeft > 0 &&
        current.name === reference.name &&
        typeof current.version === 'number' &&
        current.version > sentVersion &&
        store && typeof store.getAccountWrites === 'function' && store.getAccountWrites() === 'on'
      ) {
        retriesLeft -= 1;
        return attempt(current.version);
      }
      return { type: 'current', home: current };
    }

    function attempt(version) {
      var sent = { name: reference.name, version: version };
      return editHome(homeId, version, requested, renameOpts()).then(function (result) {
        if (result && result.ok === true && result.data) {
          refreshPageHome(result.data);
          return { type: 'renamed', home: result.data };
        }
        var status = result && result.status;
        if (status === 'writes_paused') return { type: 'paused' };
        if (status === 'conflict') {
          var current = result.data && result.data.current;
          if (current && typeof current.version === 'number') return afterConflict(current, version);
          return readHomeWithin(homeId).then(function (read) {
            if (read.state === 'present') {
              // The read counts in place of an unusable conflict record.
              if (read.home.version === version && read.home.name !== requested) {
                // Not newer than what was sent: nothing to resend safely.
                refreshPageHome(read.home);
                return { type: 'current', home: read.home };
              }
              return afterConflict(read.home, version);
            }
            if (read.state === 'absent') return resolveRename404(homeId, requested, sent);
            return { type: 'check', check: 'home', definite: true, sent: sent };
          });
        }
        if (status === 'cloud_unavailable') return resolveRename404(homeId, requested, sent);
        if (result && result.outcomeUnknown) {
          return readHomeWithin(homeId).then(function (read) {
            if (read.state === 'present') {
              refreshPageHome(read.home);
              return describeRead(sent, requested, read.home);
            }
            if (read.state === 'absent') return resolveRename404(homeId, requested, sent, false);
            return { type: 'check', check: 'home', definite: false, sent: sent };
          });
        }
        return { type: 'refused', reason: refusedReason(result) };
      });
    }

    return attempt(reference.version);
  }

  // "Check again": reads only, never sends. check: 'home' | 'lists'.
  function checkHomeRename(homeId, requested, sent, check, definite) {
    if (check === 'lists') return resolveRename404(homeId, requested, sent, !!definite);
    return readHomeWithin(homeId).then(function (read) {
      if (read.state === 'present') {
        refreshPageHome(read.home);
        return describeRead(sent, requested, read.home);
      }
      if (read.state === 'absent') return resolveRename404(homeId, requested, sent, !!definite);
      return { type: 'check', check: 'home', definite: !!definite, sent: sent };
    });
  }

  function formatSavedAt(savedAt) {
    if (typeof savedAt !== 'number' || !isFinite(savedAt) || savedAt <= 0) return '';
    try {
      return new Date(savedAt).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch (e) {
      return '';
    }
  }

  // Copy for the "Add to Project" helper line (Phase 6A copy correction).
  function storageNoteText(item, cloudActive) {
    return cloudActive
      ? 'This ' + item + ' is saved to your Môdern Studio™ account when you select “Add to Project.” Changes you make afterward aren’t saved until you add it again.'
      : 'This ' + item + ' is saved to a Project in this browser when you select “Add to Project.”';
  }

  // ------------------------------------------------------------
  // Shared "Add to Project" controller for Mood Board and Space Planner
  // (cloud mode only; the browser-only path stays in each page).
  // ------------------------------------------------------------
  //
  // config = {
  //   field: 'moodBoard' | 'spacePlan',
  //   label: 'Mood Board' | 'Space Plan',   item: 'board' | 'plan',
  //   noteEl, addBtn, selectEl,
  //   buildValue()            -> the Mood Board / Space Plan currently on the page
  //   summarize(value)        -> short text summary of a saved or on-page value
  //   defaultName()           -> name for a new Project
  //   newProjectInput(name, value) -> createProject() input
  //   onSaved(project)        -> the page's existing success rendering
  //   refreshProjectList()    -> rebuild the page's Project dropdown
  // }
  function createToolSaver(config) {
    var busy = false;
    var unresolved = false;
    var panel = null;
    var label = config.label;
    var item = config.item;
    // Phase 6A-2 (revision): Projects with an unresolved "different saved
    // value" conflict, keyed by id. The value is the canonical form of the
    // saved field the customer's original action was based on (the conflict
    // baseline). While an entry exists, the ordinary "Add to Project" button
    // never writes to that Project: it shows the choice again. An entry is
    // cleared only by a confirmed save to that Project (Replace), or when the
    // saved field is found back at the baseline. Closing the panel (Project
    // change, new board/plan, Start Over, Keep) does not clear it. Memory
    // only.
    var conflictBaselines = {};

    // Phase 6A-2 (review): a recovery read (after an unknown outcome, a
    // "Check again", or the list check after a write 404) refreshes the
    // page's copy of the Project. If the saved field it shows is neither
    // what the customer's action was based on nor what this page tried to
    // save, it was changed elsewhere: record the conflict baseline so the
    // next ordinary "Add to Project" shows the choice instead of writing.
    // If it shows this page's own value, the earlier write landed, and any
    // baseline for that Project is cleared.
    function noteRecoveredRecord(reference, value, current) {
      if (!reference || !current) return;
      var saved = canon(current[config.field]);
      if (value !== undefined && saved === canon(value)) {
        delete conflictBaselines[reference.id];
        return;
      }
      if (saved !== canon(reference[config.field]) &&
          !Object.prototype.hasOwnProperty.call(conflictBaselines, reference.id)) {
        conflictBaselines[reference.id] = canon(reference[config.field]);
      }
    }

    function onBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = '';
      return '';
    }

    // Leave-page warning while a failed save or conflict is unresolved.
    // Browsers show their own generic text; iOS Safari usually ignores it.
    function setUnresolved(on) {
      if (on === unresolved) return;
      unresolved = on;
      if (on) window.addEventListener('beforeunload', onBeforeUnload);
      else window.removeEventListener('beforeunload', onBeforeUnload);
    }

    function ensurePanel() {
      if (panel) return panel;
      panel = document.createElement('div');
      panel.className = 'studio-recovery';
      panel.setAttribute('data-studio-recovery', '');
      panel.setAttribute('role', 'status');
      panel.setAttribute('aria-live', 'polite');
      panel.hidden = true;
      var note = config.noteEl;
      if (note && note.parentNode) note.parentNode.insertBefore(panel, note.nextSibling);
      return panel;
    }

    function hidePanel() {
      if (!panel) return;
      panel.hidden = true;
      panel.textContent = '';
    }

    function showNote(text) {
      hidePanel();
      if (!config.noteEl) return;
      config.noteEl.hidden = false;
      config.noteEl.textContent = text;
    }

    function setBusy(on) {
      busy = on;
      if (config.addBtn) config.addBtn.disabled = on;
      if (panel) {
        Array.prototype.forEach.call(panel.querySelectorAll('button'), function (b) { b.disabled = on; });
      }
    }

    // Renders { title?, lines: [text], compare: [text], actions: [{ text, primary, run }] }
    function showPanel(spec) {
      var p = ensurePanel();
      if (config.noteEl) config.noteEl.hidden = true;
      p.textContent = '';
      if (spec.title) {
        var title = document.createElement('p');
        title.className = 'studio-recovery__title';
        title.textContent = spec.title;
        p.appendChild(title);
      }
      (spec.lines || []).forEach(function (text) {
        var line = document.createElement('p');
        line.className = 'studio-recovery__body';
        line.textContent = text;
        p.appendChild(line);
      });
      if (spec.compare && spec.compare.length) {
        var list = document.createElement('ul');
        list.className = 'studio-recovery__compare';
        spec.compare.forEach(function (text) {
          var li = document.createElement('li');
          li.textContent = text;
          list.appendChild(li);
        });
        p.appendChild(list);
      }
      if (spec.actions && spec.actions.length) {
        var actions = document.createElement('div');
        actions.className = 'studio-recovery__actions';
        spec.actions.forEach(function (action) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'button ' + (action.primary ? 'button--primary' : 'button--secondary');
          b.setAttribute('data-recovery-action', action.id);
          b.textContent = action.text;
          b.addEventListener('click', function () {
            if (!busy) action.run();
          });
          actions.appendChild(b);
        });
        p.appendChild(actions);
      }
      p.hidden = false;
    }

    function savedPart(value) {
      var when = value && formatSavedAt(value.savedAt);
      return when ? ', saved ' + when : '';
    }

    function describeSaved(current) {
      var value = current && current[config.field];
      return value ? config.summarize(value) + savedPart(value) : null;
    }

    function saveAsNewAction() {
      return { id: 'save-new', text: 'Save as a new Project', run: saveAsNew };
    }

    function showSuccess(project, replaced) {
      delete conflictBaselines[project.id];
      setUnresolved(false);
      hidePanel();
      config.onSaved(project);
      if (replaced && config.noteEl) {
        var note = config.noteEl;
        note.hidden = false;
        note.textContent = 'Saved to “' + project.name + '.” It replaced the earlier ' + label + '. ';
        var link = document.createElement('a');
        link.href = '/pages/my-projects';
        link.textContent = 'View My Projects →';
        note.appendChild(link);
      }
    }

    function showConflict(current, value, fieldChanged, changedAgain) {
      setUnresolved(true);
      var name = current.name;
      if (!fieldChanged) {
        showPanel({
          title: 'This Project is being changed somewhere else',
          lines: ['“' + name + '” changed again while we were saving, for example from another tab. Nothing was overwritten, and your ' + item + ' is still on this page.'],
          actions: [
            { id: 'try-again', text: 'Try again', primary: true, run: function () { runAttach(current, config.buildValue(), 'add'); } },
            saveAsNewAction(),
            { id: 'cancel', text: 'Cancel', run: function () { keep(current); } },
          ],
        });
        return;
      }
      var savedValue = current[config.field];
      var lines = [];
      if (changedAgain) lines.push('The saved ' + label + ' changed again while you were deciding. Here’s the newest version.');
      lines.push(savedValue
        ? '“' + name + '” already has a different ' + label + savedPart(savedValue) + ', probably from another tab or device. Your ' + item + ' on this page hasn’t been saved to it.'
        : 'The ' + label + ' saved in “' + name + '” was removed from another tab or device. Your ' + item + ' on this page hasn’t been saved to it.');
      var compare = [];
      if (savedValue) compare.push('Saved: ' + config.summarize(savedValue));
      compare.push('Yours: ' + config.summarize(value));
      showPanel({
        title: 'This Project was updated somewhere else',
        lines: lines,
        compare: compare,
        actions: [
          {
            id: 'replace',
            text: savedValue ? 'Replace with this ' + item : 'Add this ' + item + ' to “' + name + '”',
            primary: true,
            run: function () { runAttach(current, config.buildValue(), 'replace'); },
          },
          saveAsNewAction(),
          { id: 'keep', text: savedValue ? 'Keep the saved one' : 'Cancel', run: function () { keep(current); } },
        ],
      });
    }

    function keep(current) {
      setUnresolved(false);
      var hasSaved = current && current[config.field];
      showNote(hasSaved
        ? 'Kept the saved ' + label + ' in “' + current.name + '.” Your ' + item + ' is still on this page.'
        : 'Nothing was changed in “' + current.name + '.” Your ' + item + ' is still on this page.');
    }

    function showCheck(reference, value, check) {
      setUnresolved(true);
      if (check && check.checked && check.current) noteRecoveredRecord(reference, value, check.current);
      var checkAgain = { id: 'check-again', text: 'Check again', primary: true, run: function () { runCheck(reference, value); } };
      if (check && check.checked && check.deleted) {
        config.refreshProjectList();
        showPanel({
          lines: ['We couldn’t confirm whether your ' + item + ' was saved, and that Project is no longer in your account. Your ' + item + ' is still on this page.'],
          actions: [saveAsNewAction()],
        });
        return;
      }
      if (check && check.checked && check.current) {
        var saved = describeSaved(check.current);
        showPanel({
          lines: [
            'We couldn’t confirm whether your ' + item + ' was saved.',
            saved
              ? 'Here’s what “' + check.current.name + '” has saved right now: ' + saved + '.'
              : '“' + check.current.name + '” doesn’t have a ' + label + ' saved right now.',
            'Your ' + item + ' is still on this page.',
          ],
          actions: [checkAgain, saveAsNewAction()],
        });
        return;
      }
      showPanel({
        lines: ['We couldn’t confirm whether your ' + item + ' was saved, and we couldn’t check your account just now. Your ' + item + ' is still on this page.'],
        actions: [checkAgain, saveAsNewAction()],
      });
    }

    function handleAttachOutcome(outcome, reference, value, mode) {
      switch (outcome.type) {
        case 'saved':
          showSuccess(outcome.project, mode === 'replace');
          return;
        case 'conflict':
          if (outcome.fieldChanged && !Object.prototype.hasOwnProperty.call(conflictBaselines, reference.id)) {
            conflictBaselines[reference.id] = canon(reference[config.field]);
          }
          showConflict(outcome.current, value, outcome.fieldChanged, mode === 'replace' && outcome.fieldChanged);
          return;
        case 'deleted':
          setUnresolved(true);
          config.refreshProjectList();
          showPanel({
            lines: ['That Project was deleted from another tab or device. Your ' + item + ' is still here.'],
            actions: [saveAsNewAction()],
          });
          return;
        case 'unavailable':
          noteRecoveredRecord(reference, value, outcome.current);
          setUnresolved(true);
          showNote('We couldn’t save to your account right now. Your ' + item + ' is still on this page.');
          return;
        case 'unreachable':
          setUnresolved(true);
          showNote('We couldn’t reach your account right now. Your ' + item + ' is still on this page.');
          return;
        case 'unknown':
          showCheck(reference, value, outcome.check);
          return;
        case 'stale':
          setUnresolved(true);
          showPanel({
            lines: ['We couldn’t check the latest version of this Project. Your ' + item + ' is still here.'],
            actions: [
              { id: 'check-again', text: 'Check again', primary: true, run: function () { runCheck(reference, value); } },
              saveAsNewAction(),
            ],
          });
          return;
        default:
          handleCommonFailure(outcome.type);
      }
    }

    function handleCommonFailure(type) {
      setUnresolved(true);
      if (type === 'not_entitled') {
        showNote('Adding to Projects requires an active Môdern Studio™ AI+ membership. Your ' + item + ' is still on this page.');
      } else if (type === 'not_authenticated') {
        showNote('Please sign in again to save to your account. Your ' + item + ' is still on this page.');
      } else {
        showNote('We couldn’t save this right now. Your ' + item + ' is still on this page. Please try again in a moment.');
      }
    }

    function runAttach(reference, value, mode) {
      setBusy(true);
      attachFieldWithRecovery({ field: config.field, reference: reference, value: value })
        .then(function (outcome) {
          setBusy(false);
          handleAttachOutcome(outcome, reference, value, mode);
        }, function () {
          setBusy(false);
          handleCommonFailure('error');
        });
    }

    function runCheck(reference, value) {
      setBusy(true);
      readProjectForCheck(reference.id).then(function (check) {
        setBusy(false);
        showCheck(reference, value, check);
      }, function () {
        setBusy(false);
        showCheck(reference, value, { checked: false });
      });
    }

    function runCreate(name, value) {
      setBusy(true);
      createProjectWithRecovery(config.newProjectInput(name, value))
        .then(function (outcome) {
          setBusy(false);
          if (outcome.type === 'saved') {
            showSuccess(outcome.project, false);
          } else if (outcome.type === 'unavailable') {
            setUnresolved(true);
            showNote('We couldn’t save to your account right now. Your ' + item + ' is still on this page.');
          } else if (outcome.type === 'unknown_create') {
            setUnresolved(true);
            config.refreshProjectList();
            showNote(outcome.refreshed
              ? 'We couldn’t confirm whether your new Project was created. We’ve refreshed your Project list — check it before adding again so you don’t create a duplicate. Your ' + item + ' is still on this page.'
              : 'We couldn’t confirm whether your new Project was created, and we couldn’t refresh your Project list. Check My Projects before adding again so you don’t create a duplicate. Your ' + item + ' is still on this page.');
          } else {
            handleCommonFailure(outcome.type);
          }
        }, function () {
          setBusy(false);
          handleCommonFailure('error');
        });
    }

    function saveAsNew() {
      runCreate(config.defaultName(), config.buildValue());
    }

    function save() {
      if (busy) return;
      var selectedId = config.selectEl ? config.selectEl.value : '';
      var value = config.buildValue();
      if (selectedId) {
        var store = projectsStore();
        var reference = store && typeof store.getProject === 'function' ? store.getProject(selectedId) : null;
        if (!reference) {
          setUnresolved(true);
          showNote('We couldn’t find that Project on this page. Choose it again, or save your ' + item + ' as a new Project.');
          return;
        }
        // Phase 6A-2 (revision): an unresolved conflict for this Project is
        // never settled by the ordinary Add button. If the saved value still
        // differs from the conflict baseline, show the choice again (no
        // request); the customer must pick Replace to overwrite it.
        if (Object.prototype.hasOwnProperty.call(conflictBaselines, selectedId)) {
          if (canon(reference[config.field]) !== conflictBaselines[selectedId]) {
            showConflict(reference, value, true, false);
            return;
          }
          delete conflictBaselines[selectedId];
        }
        runAttach(reference, value, 'add');
        return;
      }
      runCreate(config.defaultName(), value);
    }

    function reset() {
      hidePanel();
      setUnresolved(false);
    }

    return {
      save: save,
      reset: reset,
      isBusy: function () { return busy; },
      hasUnresolved: function () { return unresolved; },
    };
  }

  // ------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------

  window.MSGCloudProjects = {
    listProjects: listProjects,
    getProject: getProject,

    createProject: createProject,
    attachMoodBoard: attachMoodBoard,
    attachSpacePlan: attachSpacePlan,
    deleteProject: deleteProject,
    assignProjectHome: assignProjectHome,

    createHome: createHome,
    deleteHome: deleteHome,
    editHome: editHome,
    listHomes: listHomes,
    getHome: getHome,

    getDiagnostics: getDiagnostics,

    // Phase 6A-2: conflict-aware recovery (see the section above).
    recovery: {
      canon: canon,
      sameValue: sameValue,
      isUsableCurrent: isUsableCurrent,
      classifyFieldConflict: classifyFieldConflict,
      classifyAssignConflict: classifyAssignConflict,
      describeProjectChanges: describeProjectChanges,
      joinPhrases: joinPhrases,
      checkProjectExists: checkProjectExists,
      refreshSnapshotFromCloud: refreshSnapshotFromCloud,
      attachFieldWithRecovery: attachFieldWithRecovery,
      createProjectWithRecovery: createProjectWithRecovery,
      readProjectForCheck: readProjectForCheck,
      formatSavedAt: formatSavedAt,
      storageNoteText: storageNoteText,
      createToolSaver: createToolSaver,
      // Phase 6C: Home rename.
      renameHomeWithRecovery: renameHomeWithRecovery,
      checkHomeRename: checkHomeRename,
    },

    // Phase 6C: the rename time limit (tests shorten it).
    _renameTiming: renameTiming,
  };
})();
